import {Card, Separator} from 'platform/components';
import {Box, HStack, Show} from 'platform/foundation';
import {match, Pattern} from 'ts-pattern';

import {ReactNode} from 'react';

import {DataGridTileLayout} from '@dms/shared';
import {DataGridTreeFolderHandle} from '@dms/shared-aftersales';

import {TestIdProps} from 'shared/web';

import {EagGridScreenWrapper} from 'features/eag-grid';

import {ExternalFilterWithFolderTree} from './ExternalFilterWithFolderTree';

export type FullscreenDataGridWrapperProps = {
  externalFilterId?: string;
  hideCardWrapper?: true;
  /**
   * Shows the filter panel and the grid as two tiles side by side (DataGridTileLayout) instead of
   * one card. Needs `externalFilterId` and a grid with the CARD wrapper style.
   */
  isTileLayout?: true;
  folderTreeRef?: (node: DataGridTreeFolderHandle) => void;
  children: ReactNode;
} & TestIdProps;

export function FullscreenDataGridWrapper(props: FullscreenDataGridWrapperProps) {
  const gridChildren = <EagGridScreenWrapper>{props.children}</EagGridScreenWrapper>;

  const content = match(props.externalFilterId)
    .with(Pattern.nullish, () => (
      <Box height="100%" width="100%">
        {gridChildren}
      </Box>
    ))
    .otherwise(() => (
      <HStack height="100%" data-testid={props['data-testid']}>
        <Show when={props.externalFilterId}>
          <ExternalFilterWithFolderTree
            ref={props.folderTreeRef}
            externalFilterId={props.externalFilterId as string}
            width={100}
            data-testid={`${props['data-testid']}-externalFilterWithFolderTree`}
          />
        </Show>

        <Separator orientation="vertical" />

        <Box height="100%" width="100%">
          {gridChildren}
        </Box>
      </HStack>
    ));

  return match(props)
    .with({isTileLayout: true, externalFilterId: Pattern.string}, (tileProps) => (
      <DataGridTileLayout
        sidePanel={
          <ExternalFilterWithFolderTree
            ref={tileProps.folderTreeRef}
            externalFilterId={tileProps.externalFilterId}
            isPanel
            data-testid={`${tileProps['data-testid']}-externalFilterWithFolderTree`}
          />
        }
        data-testid={tileProps['data-testid']}
      >
        {gridChildren}
      </DataGridTileLayout>
    ))
    .with({hideCardWrapper: true}, () => content)
    .otherwise(() => <Card isFullHeight>{content}</Card>);
}
